/**
 * BoardingPassPdf.js
 *
 * The guest's real boarding pass as a PDF, laid out as boarding-pass-mobile-2.html: the phone pass design, with a QR code of their PNR in place of the
 * decorative barcode (the counter at the door scans it, like a Wallet pass). Used while Google Wallet can't be
 * offered yet. Drawn off screen at full size only while making the PDF, like ShareablePass.
 */

import React, { useEffect, useRef, useState } from "react";
import { toJpeg } from "html-to-image";
import { jsPDF } from "jspdf";
import BoardingPass from "./BoardingPass";
import QrCode from "./QrCode";

const BACKDROP = "#E9E4DA";
// The page, as boarding-pass-mobile-2.html: 390 x 696, the 358px phone pass with 16px of backdrop either side
// (the pass adds the 24px top and bottom itself)
const PAGE_WIDTH = 390;
const SIDE_MARGIN = 16;
const QR_SIZE = 118;

/**
 * @param {Object} pass     -   { pnr, fullName, seat, fromCode, fromCity, colour }
 * @returns {Object}        -   { download: makes and saves the PDF, resolving true if it worked; drawing: render it anywhere on the page }
 */
export const useBoardingPassPdf = (pass) => {
    const [drawing, setDrawing] = useState(false);
    const [qrReady, setQrReady] = useState(false);
    const ref = useRef(null);
    const resolveRef = useRef(null);

    // Once the hidden pass and its QR code are on the page, turn it into a PDF the size of the pass and save it
    useEffect(() => {
        if (!drawing || !qrReady) return;
        const save = async () => {
            let saved = false;
            try {
                await document.fonts.ready;
                const { offsetWidth: width, offsetHeight: height } = ref.current;
                // A high-quality JPEG keeps the file small enough to share (a PNG made it ~7 MB) and the QR code sharp
                const image = await toJpeg(ref.current, { pixelRatio: 2.5, quality: 0.92, backgroundColor: BACKDROP });
                const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: [width, height], hotfixes: ["px_scaling"], compress: true });
                pdf.addImage(image, "JPEG", 0, 0, width, height);
                pdf.save(`YJ030-boarding-pass-${pass.fullName.replace(/\s+/g, "-")}.pdf`);
                saved = true;
            } catch (err) {
                console.error("Error making the boarding pass PDF:", err);
            } finally {
                setDrawing(false);
                setQrReady(false);
                resolveRef.current?.(saved);
            }
        };
        save();
    }, [drawing, qrReady, pass?.fullName]);

    return {
        download: () => new Promise((resolve) => {
            resolveRef.current = resolve;
            setDrawing(true);
        }),
        drawing: drawing && pass && (
            <div style={{ position: "fixed", left: -10000, top: 0 }} aria-hidden="true">
                <div ref={ref} style={{ width: PAGE_WIDTH, padding: `0 ${SIDE_MARGIN}px`, boxSizing: "border-box", background: BACKDROP }}>
                    <BoardingPass pass={pass} layout={{ mobile: true, scale: 1 }} qr={<QrCode value={pass.pnr} size={QR_SIZE} onReady={() => setQrReady(true)} />} />
                </div>
            </div>
        ),
    };
};
